import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { applyTemplate, NOTE_TEMPLATE_FIELDS, type NoteTemplateField } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface NoteTemplateRow {
  id: string;
  name: string;
  ownerId: string | null;
  fields: Partial<Record<NoteTemplateField, string>>;
}

type Notes = Record<string, string>;

export function pickTemplateFields(notes: Notes): Partial<Record<NoteTemplateField, string>> {
  const out: Partial<Record<NoteTemplateField, string>> = {};
  for (const field of NOTE_TEMPLATE_FIELDS) {
    const text = notes[field]?.trim();
    if (text) out[field] = text;
  }
  return out;
}

export function NoteTemplatesMenu({ notes, onApply }: { notes: Notes; onApply: (next: Notes) => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({ queryKey: ['note-templates'], queryFn: () => get<NoteTemplateRow[]>('/note-templates'), staleTime: 60_000 });

  const save = useMutation({
    mutationFn: () => post('/note-templates', { name: name.trim(), shared, fields: pickTemplateFields(notes) }),
    onSuccess: () => {
      toast.success(tr('opd', 'template_saved'));
      setSaving(false);
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['note-templates'] });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <div className="flex items-center gap-2">
      <Select
        value=""
        className="h-8 w-48"
        aria-label={tr('opd', 'insert_template')}
        onChange={(e) => {
          const chosen = templates.data?.find((template) => template.id === e.target.value);
          if (chosen) onApply({ ...notes, ...(applyTemplate(notes, chosen.fields) as Notes) });
        }}
      >
        <option value="">{tr('opd', 'insert_template_2')}</option>
        {templates.data?.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
            {template.ownerId ? '' : ' (shared)'}
          </option>
        ))}
      </Select>
      <Button size="sm" variant="ghost" onClick={() => setSaving(true)} disabled={Object.keys(pickTemplateFields(notes)).length === 0}>
        {tr('opd', 'save_as_template')}
      </Button>
      {saving && (
        <Dialog
          open
          onClose={() => setSaving(false)}
          title={tr('opd', 'save_as_template')}
          description={tr('opd', 'the_filled_parts_of_this_note_become_a_template')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setSaving(false)}>
                {tr('opd', 'cancel')}
              </Button>
              <Button onClick={() => save.mutate()} loading={save.isPending} disabled={name.trim().length < 2}>
                {tr('opd', 'save')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Field label={tr('opd', 'name')} required>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
            </Field>
            {can('template.manage') && <Checkbox checked={shared} onCheckedChange={setShared} label={tr('opd', 'share_with_every_doctor')} />}
            {error && <Alert variant="destructive">{error}</Alert>}
          </div>
        </Dialog>
      )}
    </div>
  );
}
